'use client';
import { min, max, noop } from '@s7/utils';
import { detectOverflow } from '../detectOverflow.js';
import { evaluate, getSide, getAlignment, getSideAxis } from '../../utils/base.js';

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
/**
 * Provides data that allows you to change the size of the floating element —
 * for instance, prevent it from overflowing the clipping boundary or match the
 * width of the reference element.
 * @see https://floating-ui.com/docs/size
 */ const size = (options = {})=>({
        name: 'size',
        options,
        fn (state) {
            return _async_to_generator(function*() {
                const { placement, rects, platform, elements } = state;
                const _evaluate = evaluate(options, state), { apply = noop } = _evaluate, detectOverflowOptions = _object_without_properties_loose(_evaluate, [
                    "apply"
                ]);
                const overflow = yield detectOverflow(state, detectOverflowOptions);
                const side = getSide(placement);
                const alignment = getAlignment(placement);
                const isYAxis = getSideAxis(placement) === 'y';
                const { width, height } = rects.floating;
                let heightSide;
                let widthSide;
                if (side === 'top' || side === 'bottom') {
                    heightSide = side;
                    widthSide = alignment === ((yield platform.isRTL == null ? void 0 : platform.isRTL.call(platform, elements.floating)) ? 'start' : 'end') ? 'left' : 'right';
                } else {
                    widthSide = side;
                    heightSide = alignment === 'end' ? 'top' : 'bottom';
                }
                const overflowAvailableHeight = height - overflow[heightSide];
                const overflowAvailableWidth = width - overflow[widthSide];
                const noShift = !state.middlewareData.shift;
                let availableHeight = overflowAvailableHeight;
                let availableWidth = overflowAvailableWidth;
                if (isYAxis) {
                    const maximumClippingWidth = width - overflow.left - overflow.right;
                    availableWidth = alignment || noShift ? min(overflowAvailableWidth, maximumClippingWidth) : maximumClippingWidth;
                } else {
                    const maximumClippingHeight = height - overflow.top - overflow.bottom;
                    availableHeight = alignment || noShift ? min(overflowAvailableHeight, maximumClippingHeight) : maximumClippingHeight;
                }
                if (noShift && !alignment) {
                    const xMin = max(overflow.left, 0);
                    const xMax = max(overflow.right, 0);
                    const yMin = max(overflow.top, 0);
                    const yMax = max(overflow.bottom, 0);
                    if (isYAxis) {
                        availableWidth = width - 2 * (xMin !== 0 || xMax !== 0 ? xMin + xMax : max(overflow.left, overflow.right));
                    } else {
                        availableHeight = height - 2 * (yMin !== 0 || yMax !== 0 ? yMin + yMax : max(overflow.top, overflow.bottom));
                    }
                }
                yield apply(_extends({}, state, {
                    availableWidth,
                    availableHeight
                }));
                const nextDimensions = yield platform.getDimensions(elements.floating);
                if (width !== nextDimensions.width || height !== nextDimensions.height) {
                    return {
                        reset: {
                            rects: true
                        }
                    };
                }
                return {};
            })();
        }
    });

export { size };
//# sourceMappingURL=size.js.map
